import { Box, Link, Grid, CircularProgress } from '@mui/material';
import React from 'react';
import DescriptionIcon from '@mui/icons-material/Description';
import { Paper, Typography } from '@mui/material';
import IconButton from '../../../components/IconButton';
import { ALLOWED_FILES_DOC, ICON_BUTTON_TYPE, MAX_FILE_SIZE } from '../../../constants';
import DragAndDrop from './DragAndDrop';
import { useRef } from 'react';
import { toast } from 'react-toastify';
import axios from 'axios';
import {
  deleteDocuments,
  downloadDocuments,
  getDocuments,
  deleteUserDocuments,
  getUserDocuments,
  signedUserUrlApi,
  addUserDocuments,
  downloadUserDocuments,
} from '../../../utils/api';
import { useMutation, useQuery } from 'react-query';
import { isEmpty } from 'lodash';
import moment from 'moment';
import { useTranslation } from 'react-i18next';

const Documents = ({ user, recipient }) => {
  const { t } = useTranslation();
  const fileRef = useRef(null);
  const [imageLoader, setImageLoader] = React.useState(false);

  const { data, isLoading, refetch } = useQuery(
    ['getDocuments'],
    () =>
      recipient?._id
        ? getDocuments({ userId: user?._id, recipientId: recipient?._id })
        : getUserDocuments({ userId: user?._id }),
    {
      retry: false,
      cacheTime: 0,
    },
  );

  const deleteDocumentsHandler = useMutation(recipient?._id ? deleteDocuments : deleteUserDocuments, {
    onSuccess: async ({ data }) => {
      refetch();
    },
    onError: (error) => {
      console.log('error =====> ', error);
    },
  });

  const downloadDocumentsHandler = useMutation(recipient?._id ? downloadDocuments : downloadUserDocuments, {
    onSuccess: async ({ data }) => {
      window.open(data, '__blank');
    },
    onError: (error) => {
      console.log('error =====> ', error);
    },
  });

  const handleSetFile = (e) => {
    const [file] = e.target.files;
    handleUploadFile(file);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
    e.dataTransfer.dropEffect = 'copy';
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();

    const [file] = Array.from(e.dataTransfer.files);
    handleUploadFile(file);
  };

  const handleUploadFile = async (file) => {
    setImageLoader(true);
    try {
      const { type, name, size } = file;

      if (size > MAX_FILE_SIZE) {
        toast.error('Max file size 5MB!');
        setImageLoader(false);
        return;
      }

      if (!ALLOWED_FILES_DOC.documents.includes(type)) {
        toast.error('File not supported!');
        setImageLoader(false);
        return;
      }

      let postURL;
      if (recipient?._id) {
        const { data } = await axios.post(
          `${process.env.REACT_APP_EXPRESS_URL}/api/canCare/caregiver/${user?._id}/signedUrl/${recipient?._id}`,
          { type, name },
          {
            headers: {
              'Content-Type': 'application/json',
              authorization: localStorage.getItem('auth'),
              'ngrok-skip-browser-warning': 'true',
            },
          },
        );
        postURL = data?.data?.postURL;
      } else {
        const { data } = await signedUserUrlApi({ userId: user?._id, type, isDocuments: true, name });
        postURL = data?.postURL;
      }

      await axios.put(postURL, file, {
        headers: {
          'Content-Type': type,
        },
      });
      if (recipient?._id) {
        await axios.post(
          `${process.env.REACT_APP_EXPRESS_URL}/api/canCare/caregiver/${user?._id}/documents/${recipient?._id}`,
          { name, type, link: postURL.split('?')[0] },
          {
            headers: {
              'Content-Type': 'application/json',
              authorization: localStorage.getItem('auth'),
            },
          },
        );
      } else {
        await addUserDocuments({ userId: user?._id, body: { name, type, link: postURL.split('?')[0] } });
      }
      refetch();
    } catch (error) {
      toast.error('Something went wrong!');
      console.log('error =====> ', error);
    } finally {
      setImageLoader(false);
    }
  };

  const handleDelete = (item) => {
    if (recipient?._id) {
      deleteDocumentsHandler.mutate({ userId: user?._id, recipientId: recipient?._id, documentId: item?._id });
    } else {
      deleteDocumentsHandler.mutate({ userId: user?._id, documentId: item?._id });
    }
  };

  const handleDownload = (item) => {
    if (recipient?._id) {
      downloadDocumentsHandler.mutate({ userId: user?._id, recipientId: recipient?._id, documentId: item?._id });
    } else {
      downloadDocumentsHandler.mutate({ userId: user?._id, documentId: item?._id });
    }
  };

  return (
    <Grid>
      <DragAndDrop
        onClick={() => fileRef.current.click()}
        handleDragOver={handleDragOver}
        handleDrop={handleDrop}
        imageLoader={imageLoader}
      />
      <input ref={fileRef} type="file" style={{ display: 'none' }} onChange={handleSetFile} />

      {isLoading && (
        <Grid marginY={10} container justifyContent={'center'}>
          <CircularProgress size={40} style={{ color: '#26786a' }} />
        </Grid>
      )}

      {!isLoading && isEmpty(data?.data) && (
        <Typography variant="h5" textAlign="center" marginTop={5}>
          {t('noDocumentsFound')}
        </Typography>
      )}

      {!isLoading &&
        !isEmpty(data?.data) &&
        data?.data.map((item, idx) => (
          <Paper
            key={idx}
            elevation={1}
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              p: 2,
              borderRadius: 2,
              backgroundColor: 'collection-1-other-colors-backgrounds-other-bg-container',
              mt: 2,
              boxShadow: 'none',
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  width: 40,
                  height: 40,
                  backgroundColor: 'collection-1-primary-color-primary-green-50',
                  borderRadius: '50%',
                }}
              >
                <DescriptionIcon sx={{ color: 'collection-1-primary-color-primary-green-500' }} />
              </Box>
              <Box>
                <Typography
                  variant="body1"
                  sx={{
                    fontWeight: 500,
                    color: 'collection-1-other-colors-text-icons-other-text-lightbg',
                  }}
                >
                  {item?.title}
                </Typography>
                <Typography
                  variant="body2"
                  sx={{
                    color: 'collection-1-other-colors-text-icons-other-text-description',
                  }}
                >
                  {moment.unix(item?.createdAt).format('YYYY-MM-DD')}
                </Typography>
              </Box>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              {/* <IconButton type={ICON_BUTTON_TYPE.eye} /> */}
              <IconButton type={ICON_BUTTON_TYPE.download} onClick={() => handleDownload(item)} />
              <IconButton type={ICON_BUTTON_TYPE.delete} onClick={() => handleDelete(item)} />
            </Box>
          </Paper>
        ))}
    </Grid>
  );
};

export default Documents;
